import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Chip, 
  Autocomplete,
  Paper,
  Typography,
  Divider,
  Grid
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

const AIDataForm = ({ onSubmit, products = [] }) => {
  // Initial form state
  const [formData, setFormData] = useState({
    product_id: '',
    occasion: [],
    situation: [],
    programs: [],
    gender: []
  });

  // Input states for tag fields
  const [occasionInput, setOccasionInput] = useState('');
  const [situationInput, setSituationInput] = useState('');
  const [programsInput, setProgramsInput] = useState('');
  const [genderInput, setGenderInput] = useState('');

  // Gender options
  const genderOptions = ['male', 'female', 'unisex'];

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Filter out empty values from arrays
    const cleanedData = {
      ...formData,
      occasion: formData.occasion.filter(item => item.trim() !== ''),
      situation: formData.situation.filter(item => item.trim() !== ''),
      programs: formData.programs.filter(item => item.trim() !== ''),
      gender: formData.gender.filter(item => item.trim() !== '')
    };

    if (onSubmit) {
      onSubmit(cleanedData);
    }

    // Reset form
    setFormData({
      product_id: '',
      occasion: [],
      situation: [],
      programs: [],
      gender: []
    });
  };

  // Add tag to array
  const addTag = (field, value, setInput) => {
    if (value.trim() !== '' && !formData[field].includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setInput('');
    }
  };

  // Remove tag from array
  const removeTag = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Handle key press for tag inputs
  const handleKeyPress = (e, field, value, setInput) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(field, value, setInput);
    }
  };

  // Add gender tag
  const addGenderTag = (gender) => {
    if (!formData.gender.includes(gender)) {
      setFormData(prev => ({
        ...prev,
        gender: [...prev.gender, gender]
      }));
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        Masho AI Database Entry
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Add AI training data for product recommendations
      </Typography>
      <Divider sx={{ my: 2 }} />

      <Box component="form" onSubmit={handleSubmit}>
        {/* Product ID Dropdown */}
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="product-select-label">Product ID *</InputLabel>
          <Select
            labelId="product-select-label"
            value={formData.product_id}
            label="Product ID *"
            onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
          >
            <MenuItem value="">
              <em>Select a product</em>
            </MenuItem>
            {products.map((product) => (
              <MenuItem key={product._id || product.id} value={product.id}>
                {product.id} - {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Occasion Tags */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Occasion
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Add occasion (e.g., Wedding, Party)"
              value={occasionInput}
              onChange={(e) => setOccasionInput(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'occasion', occasionInput, setOccasionInput)}
            />
            <Button
              variant="contained"
              onClick={() => addTag('occasion', occasionInput, setOccasionInput)}
              disabled={!occasionInput.trim()}
            >
              <AddIcon />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.occasion.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => removeTag('occasion', index)}
                color="primary"
                variant="outlined"
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        </Box>

        {/* Situation Tags */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Situation
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Add situation (e.g., Date Night, Office)"
              value={situationInput}
              onChange={(e) => setSituationInput(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'situation', situationInput, setSituationInput)}
            />
            <Button
              variant="contained"
              onClick={() => addTag('situation', situationInput, setSituationInput)}
              disabled={!situationInput.trim()}
            >
              <AddIcon />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.situation.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => removeTag('situation', index)}
                color="secondary"
                variant="outlined"
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        </Box>

        {/* Programs Tags */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Programs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Add program (e.g., Casual, Formal)"
              value={programsInput}
              onChange={(e) => setProgramsInput(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'programs', programsInput, setProgramsInput)}
            />
            <Button
              variant="contained"
              onClick={() => addTag('programs', programsInput, setProgramsInput)}
              disabled={!programsInput.trim()}
            >
              <AddIcon />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.programs.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => removeTag('programs', index)}
                color="success"
                variant="outlined"
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        </Box>

        {/* Gender Tags */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Gender
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                freeSolo
                options={genderOptions}
                value={formData.gender}
                onChange={(e, newValue) => {
                  // Filter out non-standard values that aren't in genderOptions
                  const filteredValues = newValue.filter(val => 
                    genderOptions.includes(val.toLowerCase()) || val.trim() === ''
                  );
                  setFormData({ ...formData, gender: filteredValues });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select or type gender"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      onDelete={() => removeTag('gender', index)}
                      color="info"
                    />
                  ))
                }
              />
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {genderOptions.map((gender) => (
              <Chip
                key={gender}
                label={`Add ${gender}`}
                onClick={() => addGenderTag(gender)}
                variant={formData.gender.includes(gender) ? "filled" : "outlined"}
                color={formData.gender.includes(gender) ? "info" : "default"}
              />
            ))}
          </Box>
        </Box>

        {/* Submit Button */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setFormData({
              product_id: '',
              occasion: [],
              situation: [],
              programs: [],
              gender: []
            })}
          >
            Clear All
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!formData.product_id}
            sx={{ minWidth: 120 }}
          >
            Save Entry
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AIDataForm;